/* eslint-disable @typescript-eslint/ban-ts-comment */
import { Box, Input, MessageBlock, SearchIcon } from '@deckee/deck-hand';
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import styled, { useTheme } from 'styled-components';
import { debounce } from 'throttle-debounce';
import { ERROR_MESSAGE__500 } from '../../../../configurations/error-messages';
import { callEndpoint, telemetry } from '../../../../services/backend';
import { selectCurrentMemberId } from '../../../../services/store/modules/backend-service';
import IconWithTextLockup from '../../../lockups/IconWithTextLockup/IconWithTextLockup';
import UserAvatar from './UserAvatar';

const ERROR_MESSAGE_SIGNAL_ADD_CONTACT =
  'There was an error selecting the Deckee profile. Please try again.';

const SearchResultRow = styled(Box)`
  cursor: pointer;
`;

export interface UserListItem {
  id: string;
  image?: string;
  title: string;
  username: string;
}

export interface UserSearchResult {
  id: string;
  image?: string;
  title: string;
  username: string;
}

interface UserLookupProps {
  onSelectUser: (memberId: string) => Promise<void>;
  filterSearchResults?: (
    matches: UserSearchResult[],
    currentMemberId: string | undefined,
  ) => UserSearchResult[];
}

const UserLookup: FC<UserLookupProps> = ({
  onSelectUser,
  filterSearchResults,
}) => {
  const topOfSearchListRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const theme = useTheme();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState<string>('');
  const [searchResults, setSearchResults] = useState<UserListItem[]>([]);

  const currentMemberId = useSelector(selectCurrentMemberId);

  const debouncedSearchChange = useMemo(
    () =>
      debounce(400, async (event) => {
        try {
          const text = event.target.value;
          const trimmed = text.charAt(0) === '@' ? text.substring(1) : text;

          if (trimmed.length > 0) {
            const matches = await callEndpoint<{ data: UserSearchResult[] }>(
              callEndpoint.endpoints.searchForUsers,
              {},
              {
                search: trimmed,
              },
            );

            if (matches) {
              setSearchResults(
                filterSearchResults
                  ? filterSearchResults(matches, currentMemberId)
                  : matches,
              );
            }
          } else {
            setSearchResults([]);
          }
        } catch (e: any) {
          telemetry(telemetry.WARN, e.message);
          setError(ERROR_MESSAGE__500);
        }
      }),
    [currentMemberId, filterSearchResults],
  );

  const onSearchChange = useCallback(
    (event) => {
      // Reset any errors when search string is changed
      setError(null);

      setSearch(event.target.value);
      debouncedSearchChange(event);
    },
    [debouncedSearchChange],
  );

  const handleSelectUser = async (memberId: string) => {
    setError(null); // This will reset any errors if trying to add user again
    setLoading(true);

    try {
      await onSelectUser(memberId);
    } catch (e: any) {
      // If there is an error scroll to the top of the search list
      topOfSearchListRef?.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start',
      });
      telemetry(telemetry.WARN, e.message);
      setError(ERROR_MESSAGE_SIGNAL_ADD_CONTACT);
    } finally {
      setLoading(false);
    }
  };

  // Focus the search input
  useEffect(() => {
    searchInputRef?.current?.focus();
  }, []);

  return (
    <Box
      ref={topOfSearchListRef}
      display="flex"
      flexDirection="column"
      height="100%"
      maxHeight="100%"
    >
      {error && (
        <MessageBlock mt={2} mx={2} type="error">
          {error}
        </MessageBlock>
      )}

      <Box m={2}>
        <Input
          inputRef={searchInputRef}
          type="text"
          placeholder="Search for a Deckee profile"
          startEnhancer={() => (
            <SearchIcon
              color={loading ? theme.colors.mediumgrey : theme.colors.primary}
            />
          )}
          value={search}
          onChange={onSearchChange}
          clearable
          disabled={loading}
        />
      </Box>

      <Box mb={2} px={3} pb={3}>
        {!loading &&
          searchResults.map((user) => (
            <SearchResultRow
              key={user.id}
              py={2}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              borderBottom="1px solid"
              borderBottomColor="line"
              onClick={() => handleSelectUser(user.id)}
            >
              <IconWithTextLockup image={<UserAvatar imageId={user.image} />}>
                <Box>
                  @{user.username} - {user.title}
                </Box>
              </IconWithTextLockup>
            </SearchResultRow>
          ))}
      </Box>
    </Box>
  );
};

UserLookup.defaultProps = {
  filterSearchResults: (matches) => matches,
};

export default UserLookup;
